import { Box, Button, Typography, styled, useMediaQuery, useTheme } from '@mui/material';
import Breadcrumbs from 'components/breadcrumbs';
import FilterSidebar from 'components/filter-sidebar';
import List from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';

import Paper from 'components/paper';
import TextField from 'components/text-field';
import { useAccountState } from 'contexts/account/account-context';
import dayjs from 'dayjs';
import { useGet } from 'hooks/use-get';
import { Search } from 'lucide-react';
import { FC, useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import _ from 'underscore';
import { clearState } from 'utils/storage';
import { getCompanyInfo } from 'views/production-systems/api/productionSystems';
import { usePondsState } from 'views/production-systems/context/ponds-context';
import { CompanyInfo } from 'views/production-systems/features/farm-overview/types/company-info.type';
import ProductionSystemsTabHeader from '../../../components/tab-header';
import { RegistrationDateType } from 'views/production-systems/types/pond.type';

type ListFarmsProps = {
  uri: string;
};

const PAGE_NAME_ID = 'productionSystems.farmOverview.farms';

const TextWrapper = styled(Typography)(({ theme }) => ({
  wordWrap: 'break-word',
  maxWidth: 200,

  [theme.breakpoints.up('md')]: {
    maxWidth: 2000,
  },
}));

const ListFarms: FC<ListFarmsProps> = ({ uri }) => {
  const intl = useIntl();
  const theme = useTheme();
  const history = useHistory();
  const { account } = useAccountState();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // #region STATE
  const [searchValue, setSearchValue] = useState('');
  const { changeRegistrationDate, setSelectedPonds } = usePondsState();
  // #endregion STATE

  // #region FETCH DATA
  const { data: companyData, isLoading } = useGet(
    ['getCompanyInfo', account.company?.companyId],
    ({ queryKey }) => getCompanyInfo(String(queryKey[1])),
    {
      enabled: !!account.company?.companyId,
    }
  );
  // #endregion FETCH DATA

  // #region RENDERS
  const renderRows = () => {
    const rows = _.chain(companyData)
      .filter((info: CompanyInfo) => info.name.toLowerCase().includes(searchValue.toLowerCase()))
      .sortBy((s) => s.name.toLowerCase())
      .value();

    return rows;
  };

  const handleClick = (company: CompanyInfo) => {
    setSelectedPonds([]);

    var regDateType = uri.includes('farm-setup')
      ? RegistrationDateType.setup
      : uri.includes('farm-overview')
        ? RegistrationDateType.overview
        : RegistrationDateType.log;

    var setRegDate = uri.includes('registration-log') ? company.lastRegistration : dayjs();

    changeRegistrationDate(dayjs(setRegDate), regDateType);
    clearState({ key: 'pondFilters' });
    history.push(`${uri}/${company.globalSiteId}`);
  };

  return (
    <Box display="flex" flexDirection="column" gap={4}>
      <ProductionSystemsTabHeader utils={<Breadcrumbs />} />
      <FilterSidebar>
        <TextField
          value={searchValue}
          onChange={(event) => setSearchValue(event.target.value)}
          endAdornment={<Search />}
          label={intl.formatMessage({ id: 'general.search' })}
        />
      </FilterSidebar>
      <Paper transparent>
        <List
          orderBy="name"
          isLoading={isLoading}
          noResult={{
            show: renderRows().length <= 0,
            text: intl.formatMessage({ id: 'general.noResults' }),
          }}
          options={[
            { id: 'name', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.name` }) },
            { id: 'numberOfPonds', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.numberOfPonds` }), align: 'right' },
            { id: 'lastRegistration', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.lastRegistrationDate` }), align: 'center' },
            { id: 'actions', label: '', autoWidth: true },
          ]}>
          {renderRows().map((company: CompanyInfo, idx) => (
            <ListRow key={idx}>
              <ListCell align="left">
                <TextWrapper>{company.name}</TextWrapper>
              </ListCell>
              <ListCell align="right">
                <TextWrapper>{company.numberOfPonds}</TextWrapper>
              </ListCell>
              <ListCell align="center">
                <TextWrapper>{company.lastRegistration ? dayjs(company.lastRegistration).format('L') : ''} </TextWrapper>
              </ListCell>
              <ListCell>
                <Button size="small" value={company.globalSiteId} onClick={() => handleClick(company)}>
                  {intl.formatMessage({ id: 'general.view' })}
                </Button>
              </ListCell>
            </ListRow>
          ))}
        </List>
      </Paper>
    </Box>
  );
};

export default ListFarms;
